import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CATS = ["housing", "food", "education", "medical", "seniors", "environment", "children", "emergency"];
const URG = ["low", "normal", "high", "emergency"];

export default function RequestHelp() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", category: "food", urgency: "normal",
    country: user?.country || "", city: user?.city || "",
    description: "", anonymous: false,
  });
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="p-16">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/help-requests", form);
      toast.success("Your request is posted.");
      nav("/find-help");
    } catch { toast.error("Could not post request."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="request-help-page" className="max-w-[720px] mx-auto px-6 py-16">
      <div className="overline mb-3">REQUEST HELP</div>
      <h1 className="font-display text-4xl md:text-5xl">Tell us what you need.</h1>
      <p className="mt-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>You can post anonymously. Our moderation team reviews for authenticity.</p>

      <form onSubmit={submit} className="mt-10 space-y-4">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Title</span>
          <input data-testid="req-title" required value={form.title} onChange={set("title")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider">Category</span>
            <select data-testid="req-cat" value={form.category} onChange={set("category")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider">Urgency</span>
            <select data-testid="req-urg" value={form.urgency} onChange={set("urgency")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
              {URG.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider">Country</span>
            <input data-testid="req-country" required value={form.country} onChange={set("country")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider">City</span>
            <input data-testid="req-city" required value={form.city} onChange={set("city")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
        </div>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider">Describe the need</span>
          <textarea data-testid="req-desc" required rows={5} value={form.description} onChange={set("description")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="req-anon" type="checkbox" checked={form.anonymous} onChange={set("anonymous")} />
          Post anonymously
        </label>
        <button data-testid="req-submit" disabled={busy} className="wofy-btn-primary justify-center disabled:opacity-60">{busy ? "Posting…" : "Post request"}</button>
      </form>
    </div>
  );
}
